import { Spacing } from '@persona-id/design-tokens';
import { ThemeIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import ToColor from 'color';
import { useCallback, useRef, useState } from 'react';
import { Color, ColorResult, SketchPicker } from 'react-color';
import { UseFieldArrayRemove, UseFormSetValue } from 'react-hook-form';
import styled from 'styled-components';

import GraphVertexIcon from 'dashboard/components/GraphVertexIcon/GraphVertexIcon';
import Tooltip from 'dashboard/components/Tooltip';
import { GraphAttributes } from 'dashboard/models/graph';
import {
  ServerColorRule,
  ServerColorRuleCondition,
  ServerColorRuleStatus,
  ServerColorRuleTag,
} from 'dashboard/models/graph-backend';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Sticker from 'design-system/components/Sticker';
import { ACCOUNT_DATUM_TYPE, DEFAULT_ACCOUNT_DATUM_TYPE_RENDER_CONFIG } from 'lib/constants/graph';
import useContainerTargetDetector from 'lib/hooks/useContainerTargetDetector';
import { useEscListener } from 'lib/hooks/useKeyListener';
import { assertNeverAndThrow } from 'lib/utils/types';

interface GraphColoringRulesRowProps {
  colorRule: ServerColorRule;
  setValue: UseFormSetValue<GraphAttributes>;
  index: number;
  removeColorRule: UseFieldArrayRemove;
  onGraphConfigEdit: () => void;
}

const Row = styled('div')`
  width: 100%;
  display: flex;
  align-items: top;
  column-gap: 20px;
  padding-right: 10px;
`;

const ColorPickerContainer = styled('div')`
  top: 225px;
  right: ${Spacing[2]};
  z-index: ${Z_INDICES.toast};
  position: 'absolute';
`;

const ColorPickerTooltip = styled(Tooltip)`
  box-shadow: none;
  padding: 0;
  margin-left: auto;
`;

const TooltipContainer = styled('div')`
  margin-left: auto;
`;

export const GraphColoringRuleRow = ({
  colorRule,
  setValue,
  index,
  removeColorRule,
  onGraphConfigEdit,
}: GraphColoringRulesRowProps) => {
  const [active, setActive] = useState(false);
  const pickerEl = useRef(null);
  const buttonEl = useRef(null);

  useContainerTargetDetector(() => setActive(false), buttonEl, pickerEl);
  useEscListener(() => setActive(false));
  const handlePickColor = useCallback(
    (colorResult: Partial<ColorResult>) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setValue && setValue(`coloring-rules.${index}.color`, colorResult.hex.toUpperCase());
      onGraphConfigEdit();
    },
    [setValue, index],
  );

  const getRuleTagOrStatus = (rule: ServerColorRule) => {
    switch (rule.type) {
      case 'tag': {
        return (rule as ServerColorRuleTag).tag;
      }
      case 'status': {
        return (rule as ServerColorRuleStatus).status;
      }
      case 'condition': {
        return (rule as ServerColorRuleCondition).condition;
      }
      default: {
        assertNeverAndThrow(rule);
      }
    }
  };

  return (
    <Row>
      <GraphVertexIcon
        version='v2'
        datumTypeToken={ACCOUNT_DATUM_TYPE.token}
        nodeRenderConfig={{
          ...DEFAULT_ACCOUNT_DATUM_TYPE_RENDER_CONFIG,
          color: ToColor(colorRule.color),
        }}
      />
      <Sticker>
        {colorRule.type}: {getRuleTagOrStatus(colorRule)}
      </Sticker>
      <TooltipContainer>
        <ColorPickerTooltip
          placement='left-start'
          hideArrow={true}
          tooltipShown={active}
          tooltip={
            <ColorPickerContainer ref={pickerEl}>
              <SketchPicker
                color={colorRule.color as unknown as Color}
                disableAlpha={true}
                presetColors={[]}
                onChangeComplete={handlePickColor}
              />
            </ColorPickerContainer>
          }
        >
          <Button
            width='icon'
            aria-label='Change Color'
            type='button'
            ref={buttonEl}
            onClick={() => setActive(true)}
            size='sm'
            id='graph__change_rule_color'
            variant='inverse'
          >
            <ThemeIcon size={5} />
          </Button>
        </ColorPickerTooltip>
      </TooltipContainer>
      <Button
        width='icon'
        aria-label='Remove Color Rule'
        type='button'
        onClick={() => {
          if (removeColorRule) {
            removeColorRule(index);
            onGraphConfigEdit();
          }
        }}
        id='graph__remove_color_rule'
        size='sm'
        variant='inverse'
      >
        <TrashIcon size={5} />
      </Button>
    </Row>
  );
};

export default GraphColoringRuleRow;
